import memoizeOne from 'memoize-one';
import React, { Component } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/CzReimbursement';
import ComplianceIcon from '~/CzSuklComplianceIcon';
import { DebounceInput } from '~/Debounce';
import { calculateProductUDTDPackagePrice } from '~/Product';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';

import styles from './styles.less';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

export type ReferencePriceValue = { value: string; parentValue: null; prompt: React.ReactNode } | null;
export type ReferencePriceInValues = {
	value: ReferencePriceValue;
	isValid: boolean;
};
export interface Values {
	referencePrice: ReferencePriceInValues;
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceInValues | ReferencePriceValue | null,
	packageSizeForSingleUnit: string
) => void;
export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	czReimbursementProduct: Product;
	values: Values;
	referenceUsualTherapeuticDailyDosePerPackage: number | null;
	disableSelection: boolean;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	style: any;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		disableSelection: false,
		highlightProduct: null,
		style: null,
	};

	resolveReferencePrice = memoizeOne((type: string | null, czReimbursementProduct: Product): ReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return (
			czReimbursementProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ??
			null
		);
	});

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			JSON.stringify(this.props.czReimbursementProduct) !== JSON.stringify(nextProps.czReimbursementProduct) ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.referenceUsualTherapeuticDailyDosePerPackage !==
				nextProps.referenceUsualTherapeuticDailyDosePerPackage ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.style !== nextProps.style ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	render() {
		const {
			czReimbursementProduct,
			values,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			style,
			checked,
			onChange,
			onSave,
			checkRow,
		} = this.props;
		const { product } = czReimbursementProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(
			values.referencePrice.value?.value || null,
			czReimbursementProduct
		);
		const prompt =
			values.referencePrice.value && referencePrice ? this.renderReferencePricePrompt(referencePrice) : null;

		return (
			<div
				className={
					czReimbursementProduct.productCompliance.isRejectRemoved
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<CountrySourceWithTooltip
							countryAlpha={product.dataset.source.country.alpha2}
							countryName={product.dataset.source.country.name}
							dataSourceShortName={product.dataset.source.shortName}
							dataSourceDescription={product.dataset.source.description}
						/>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{czReimbursementProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) => onChange(czReimbursementProduct, values.referencePrice.value, value)}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{czReimbursementProduct.referencePrices.length > 1 ? (
						this.renderReferencePriceSelect(prompt)
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
					{czReimbursementProduct.singleUnitPackageSize && (
						<TailwindTooltip
							reference={<Icon name='red-exclamation-mark'>!</Icon>}
							displayClass='!absolute top-4 right-0 inline-block h-4 align-top'
							position='top'
						>
							Single unit package size
						</TailwindTooltip>
					)}
				</div>
				<div className={styles.pricePerUtddCell}>
					<Link
						data-testid='calculated-price'
						styles={{ button: styles.priceCellContent }}
						tabIndex={-1}
						to={detailLinkString}
					>
						{this.renderPackagePrice(referencePrice)}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ComplianceIcon compliance={czReimbursementProduct.productCompliance} />
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
			</div>
		);
	}

	renderReferencePriceSelect(prompt: React.ReactNode) {
		const { czReimbursementProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) => onChange(czReimbursementProduct, value, values.packageSizeForSingleUnit.value)}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{czReimbursementProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
								<div>
									<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	renderReferencePricePrompt(price: ReferencePrice) {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
				</div>
			</abbr>
		);
	}

	renderPackagePrice(referencePrice: ReferencePrice | null) {
		const { referenceUsualTherapeuticDailyDosePerPackage } = this.props;

		if (!referencePrice || !referenceUsualTherapeuticDailyDosePerPackage) {
			return null;
		}

		const { calculatedPrice, sourcePrice } = referencePrice;

		return (
			<div data-testid='cell-calc-price'>
				<div>
					{calculateProductUDTDPackagePrice(
						calculatedPrice.price,
						referenceUsualTherapeuticDailyDosePerPackage
					).roundedHumanFormat()}
				</div>
				<div>
					<strong>
						{calculateProductUDTDPackagePrice(
							sourcePrice.price,
							referenceUsualTherapeuticDailyDosePerPackage
						).roundedHumanFormat()}
					</strong>
				</div>
			</div>
		);
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement>) => {
		const { czReimbursementProduct, onSelect } = this.props;
		onSelect(czReimbursementProduct, checked, event.nativeEvent.shiftKey);
	};
}
